import { useState, useEffect, useCallback } from 'react';
import {
  Card,
  Avatar,
  Button,
  Container,
  Typography,
  Box,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  IconButton,
  Chip,
  Stack,
  Divider,
  Grid
} from '@mui/material';
import { LoadingButton } from '@mui/lab';
import { DataGrid } from '@mui/x-data-grid';
import { LocalizationProvider, DatePicker } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import PropTypes from 'prop-types';

import axios, { getErrorMessage } from 'utils/axios';
import useIsMountedRef from 'hooks/useIsMountedRef';
import { fDate } from 'utils/formatTime';
import { PATH_DASHBOARD } from '../../routes/paths';
import useSettings from '../../hooks/useSettings';
import { useSnackbar } from 'notistack';
import Page from '../../components/Page';
import Iconify from '../../components/Iconify';
import HeaderBreadcrumbs from '../../components/HeaderBreadcrumbs';
import { removeTrailingZeros } from 'utils/formatNumber';
import BlockchainTransactionDialog from 'sections/@dashboard/BlockchainTransactionDialog';
import SearchQueryDailog from 'sections/@dashboard/SearchQueryDailog';
import ProfileDialog from 'sections/@dashboard/user/ProfileDialog';
import TransactionSideDrawer from 'sections/admin/TransactionSideDrawer';
import CryptoOpsDrawer from 'sections/admin/blockchain/CryptoOpsDrawer';

function FilterChipRow({ filters, onClearOne, onClearAll }) {
  const active = [];

  if (filters.user_id) active.push({ key: 'user_id', label: `User: ${filters.user_id}` });
  if (filters.txid) active.push({ key: 'txid', label: `TxID: ${filters.txid}` });
  if (filters.start_date) active.push({ key: 'start_date', label: `From: ${new Date(filters.start_date).toLocaleDateString()}` });
  if (filters.end_date) active.push({ key: 'end_date', label: `To: ${new Date(filters.end_date).toLocaleDateString()}` });

  if (!active.length) return null;

  return (
    <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
      {active.map((item) => (
        <Chip
          key={item.key}
          label={item.label}
          onDelete={() => onClearOne(item.key)}
          variant="outlined"
          size="small"
          sx={{ mb: 1 }}
        />
      ))}
      <Button size="small" onClick={onClearAll}>
        Clear all
      </Button>
    </Stack>
  );
}

FilterChipRow.propTypes = {
  filters: PropTypes.object.isRequired,
  onClearOne: PropTypes.func.isRequired,
  onClearAll: PropTypes.func.isRequired,
};

export default function BlockchainTransactionHistory() {
  const { themeStretch } = useSettings();
  const isMountedRef = useIsMountedRef();
  const { enqueueSnackbar } = useSnackbar();

  const [filters, setFilters] = useState({
    user_id: '',
    txid: '',
    start_date: null,
    end_date: null,
    sort_order: 'desc',
    page: 0,
    page_size: 50,
  });

  const [state, setState] = useState({ loading: false, loadComplete: false, error: null });
  const [data, setData] = useState({ data: [], current_page: 1, total_pages: null, total_size: 0, page_size: 0 });
  const [dailog, setDailog] = useState({ open: false });
  const [search, setSearch] = useState({ open: false, query: '', list: [] });
  const [profileDialog, setProfileDialog] = useState({ open: false, user: '' });
  const [trans, setTrans] = useState({ open: false, txid: '' });
  const [selectedReference, setSelectedReference] = useState('');
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [opsDrawer, setOpsDrawer] = useState({ open: false, row: null });

  const loadData = useCallback(async (page = null) => {
    setState((prev) => ({ ...prev, loading: true, loadComplete: false, error: null }));

    const sdate = filters.start_date ? new Date(filters.start_date).toISOString().split('T')[0] : '';
    const edate = filters.end_date ? new Date(filters.end_date).toISOString().split('T')[0] : '';

    try {
      let query = `?sort_order=${filters.sort_order}&page=${page ?? filters.page + 1}&page_size=${filters.page_size}`;
      if (filters.user_id) query += `&user_id=${filters.user_id}`;
      if (sdate) query += `&start_date=${sdate}`;
      if (edate) query += `&end_date=${edate}`;

      const response = await axios.get(`/transactions/blockchain${query}`);

      if (isMountedRef.current) {
        setData(response.data);
      }
      setState((prev) => ({ ...prev, loading: false, loadComplete: true, error: null }));
    } catch (error) {
      setState((prev) => ({
        ...prev,
        loading: false,
        loadComplete: false,
        error: getErrorMessage(error),
      }));
    }
  }, [filters, isMountedRef]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const rows = search.open ? search.list : data.data;

  const columns = [
    {
      field: 'name',
      headerName: 'User',
      width: 240,
      renderCell: (params) => (
        <Stack direction="row" spacing={1.25} alignItems="center">
          <IconButton
            color="primary"
            size="small"
            onClick={(e) => {
              e.stopPropagation();
              setProfileDialog({ open: true, user: { user_id: params.row.user_id } });
            }}
          >
            <Iconify icon="ph:user-thin" sx={{ width: 22, height: 22 }} />
          </IconButton>
          <Box>
            <Typography variant="subtitle2" noWrap>
              {params.row.first_name} {params.row.last_name}
            </Typography>
          </Box>
        </Stack>
      ),
    },
    {
      field: 'asset',
      headerName: 'Asset',
      width: 200,
      renderCell: (params) => (
        <Stack direction="row" spacing={1.5} alignItems="center">
          <Avatar alt={params.row.name} src={params.row.image_url} sx={{ width: 32, height: 32 }} />
          <Box>
            <Typography variant="subtitle2">{params.row.name}</Typography>
            <Typography variant="caption" color="text.secondary">
              {removeTrailingZeros(params.row.amount)}
            </Typography>
          </Box>
        </Stack>
      ),
    },
    {
      field: 'reference',
      headerName: 'Reference',
      flex: 1.2,
      minWidth: 220,
      renderCell: (params) => (
        <Stack direction="row" spacing={1} alignItems="center">
          <IconButton
            color="primary"
            size="small"
            onClick={(e) => {
              e.stopPropagation();
              setSelectedReference(params.row.reference);
              setDrawerOpen(true);
            }}
          >
            <Iconify icon="ion:open-outline" sx={{ width: 20, height: 20 }} />
          </IconButton>
          <Typography variant="body2" sx={{ fontFamily: 'monospace' }} noWrap>
            {params.row.reference}
          </Typography>
        </Stack>
      ),
    },
    // {
    //   field: 'direction',
    //   headerName: 'Flow',
    //   width: 120,
    //   renderCell: (params) => (
    //     <Chip
    //       label={params.value?.toUpperCase()}
    //       color={params.value === 'credit' ? 'success' : 'error'}
    //       size="small"
    //       variant="soft"
    //     />
    //   ),
    // },
    {
      field: 'amount',
      headerName: 'Quantity',
      width: 160,
      renderCell: (params) => (
        <Stack direction="row" spacing={1} alignItems="center">
          <IconButton
            color="primary"
            size="small"
            onClick={(e) => {
              e.stopPropagation();
              setTrans({ open: true, txid: params.row.txid });
            }}
          >
            <Iconify icon="eva:expand-outline" sx={{ width: 20, height: 20 }} />
          </IconButton>
          <Box sx={{ ml: 1 }}>
            <Typography
              variant="body2"
              color={params.row.direction === 'debit' ? 'error.main' : 'success.main'}
              fontWeight={600}
            >
              {removeTrailingZeros(params.row.amount)}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {params.row.currency}
            </Typography>
          </Box>
        </Stack>
      ),
    },
    {
      field: 'actions',
      headerName: 'Ops',
      width: 150,
      sortable: false,
      filterable: false,
      renderCell: (params) => (
        <Button
          size="small"
          variant="contained"
          color="primary"
          onClick={(event) => {
            event.stopPropagation();
            setOpsDrawer({ open: true, row: params.row });
          }}
        >
          Open Drawer
        </Button>
      ),
    },
    {
      field: 'confirmations',
      headerName: 'Confirmations',
      width: 160,
      renderCell: (params) => (
        <Chip
          label={`${params.value} / ${params.row.target_confirmations}`}
          size="small"
          variant="outlined"
        />
      ),
    },
    {
      field: 'status',
      headerName: 'Status',
      width: 140,
      renderCell: (params) => (
        <Chip label={params.value} size="small" color="primary" variant="soft" />
      ),
    },
    {
      field: 'swap_status',
      headerName: 'Swap Status',
      width: 140,
      renderCell: (params) => (
        <Chip label={params.value} size="small" color="info" variant="soft" />
      ),
    },
    {
      field: 'created_at',
      headerName: 'Created',
      width: 180,
      valueFormatter: (params) => fDate(params.value),
    },
  ];

  const getRowClassName = (params) => {
    return params.row.direction === 'credit' ? 'credit-row' : 'debit-row';
  };

  const clearOne = (key) => {
    setFilters((prev) => ({ ...prev, [key]: '', page: 0 }));
  };

  const clearAll = () => {
    setFilters({
      user_id: '',
      txid: '',
      start_date: null,
      end_date: null,
      sort_order: 'desc',
      page: 0,
      page_size: 50,
    });
  };

  const handleSearchTxid = (query) => {
    setSearch({ open: true, query, list: [] });
    setState((prev) => ({ ...prev, loading: true }));

    axios
      .get(`/transactions/blockchain/${encodeURIComponent(query)}`)
      .then((response) => {
        setSearch({ open: true, query, list: response.data ? [response.data] : [] });
        setState((prev) => ({ ...prev, loading: false }));
      })
      .catch((error) => {
        setState((prev) => ({ ...prev, loading: false }));
        enqueueSnackbar(getErrorMessage(error), { variant: 'warning' });
      });
  };

  return (
    <Page title="Blockchain Transaction History">
      <Container maxWidth={themeStretch ? false : 'lg'}>
        <HeaderBreadcrumbs
          heading="Blockchain Transaction History"
          links={[
            { name: 'Blockchain transactions', href: PATH_DASHBOARD.blockchainOps.root },
            { name: 'All transactions' },
          ]}
          action={
            <Button
              variant="contained"
              startIcon={<Iconify icon="cil:search" />}
              onClick={() => setDailog({ open: true })}
            >
              Search TxID
            </Button>
          }
        />

        {state.error && (
          <Card sx={{ mb: 3 }}>
            <Box p={3} display="flex" justifyContent="center">
              <Typography color="error">{state.error}</Typography>
            </Box>
          </Card>
        )}

        <Card sx={{ borderRadius: 2.5 }}>
          {search.open && (
            <Box sx={{ p: 2, bgcolor: 'background.neutral' }}>
              <Stack direction="row" justifyContent="space-between" alignItems="center">
                <Typography variant="body2">
                  Showing results for TxID: <strong>{search.query}</strong>
                </Typography>
                <LoadingButton
                  size="small"
                  startIcon={<Iconify icon="carbon:close-filled" />}
                  onClick={() => setSearch({ open: false, query: '', list: [] })}
                  variant="outlined"
                  color="error"
                >
                  Close search
                </LoadingButton>
              </Stack>
            </Box>
          )}

          {!search.open && (
            <Box sx={{ p: 3 }}>
              <FilterChipRow filters={filters} onClearOne={clearOne} onClearAll={clearAll} />

              <Divider sx={{ my: 2 }} />

              <Grid container spacing={2}>
                <Grid item xs={12} md={3}>
                  <TextField
                    fullWidth
                    size="small"
                    label="User ID"
                    value={filters.user_id}
                    onChange={(e) => setFilters({ ...filters, user_id: e.target.value, page: 0 })}
                  />
                </Grid>
                <Grid item xs={12} md={3}>
                  <LocalizationProvider dateAdapter={AdapterDateFns}>
                    <DatePicker
                      label="From date"
                      value={filters.start_date ? new Date(filters.start_date) : null}
                      onChange={(newValue) =>
                        setFilters({ ...filters, start_date: newValue, page: 0 })
                      }
                      renderInput={(params) => <TextField {...params} fullWidth size="small" />}
                    />
                  </LocalizationProvider>
                </Grid>
                <Grid item xs={12} md={3}>
                  <LocalizationProvider dateAdapter={AdapterDateFns}>
                    <DatePicker
                      label="To date"
                      value={filters.end_date ? new Date(filters.end_date) : null}
                      onChange={(newValue) =>
                        setFilters({ ...filters, end_date: newValue, page: 0 })
                      }
                      renderInput={(params) => <TextField {...params} fullWidth size="small" />}
                    />
                  </LocalizationProvider>
                </Grid>
                <Grid item xs={12} md={3}>
                  <FormControl fullWidth size="small">
                    <InputLabel>Sort order</InputLabel>
                    <Select
                      value={filters.sort_order}
                      label="Sort order"
                      onChange={(e) => setFilters({ ...filters, sort_order: e.target.value, page: 0 })}
                    >
                      <MenuItem value="asc">Ascending</MenuItem>
                      <MenuItem value="desc">Descending</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
              </Grid>

              <Stack direction="row" spacing={2} sx={{ mt: 2.5, justifyContent: 'flex-end' }}>
                <LoadingButton
                  variant="contained"
                  loading={state.loading}
                  onClick={() => loadData()}
                >
                  Apply Filters
                </LoadingButton>
              </Stack>
            </Box>
          )}

          <Divider />

          <Box sx={{ height: 800, width: '100%' }}>
            <DataGrid
              rows={rows}
              columns={columns}
              loading={state.loading}
              disableRowSelectionOnClick
              paginationMode="server"
              rowCount={data.total_size}
              page={data.current_page - 1}
              pageSize={data.page_size}
              rowsPerPageOptions={[]}
              onPageChange={(newPage) => loadData(newPage + 1)}
              getRowClassName={getRowClassName}
              // sx={{
              //   '& .credit-row': {
              //     bgcolor: (theme) => `${theme.palette.success.lighter} !important`,
              //     '&:hover': {
              //       bgcolor: (theme) => `${theme.palette.success.lightest} !important`,
              //     },
              //   },
              //   '& .debit-row': {
              //     bgcolor: (theme) => `${theme.palette.error.lighter} !important`,
              //     '&:hover': {
              //       bgcolor: (theme) => `${theme.palette.error.lightest} !important`,
              //     },
              //   },
              // }}
              slots={{
                noRowsOverlay: () => (
                  <Stack alignItems="center" justifyContent="center" sx={{ height: '100%', px: 3 }}>
                    <Typography variant="subtitle2">No blockchain transactions</Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 1, textAlign: 'center' }}>
                      {search.open
                        ? `No transactions found for TxID: ${search.query}`
                        : 'No blockchain transactions returned for the current filters.'}
                    </Typography>
                  </Stack>
                ),
              }}
            />
          </Box>
        </Card>
      </Container>

      {dailog.open && (
        <SearchQueryDailog
          open={dailog.open}
          hint="Search database for matching records"
          label="Enter TxID"
          handleClose={() => setDailog({ open: false })}
          onSubmit={(value) => {
            setDailog({ open: false });
            handleSearchTxid(value);
          }}
        />
      )}

      {profileDialog.open && (
        <ProfileDialog
          user={profileDialog.user}
          refresh
          handleClose={() => setProfileDialog({ open: false })}
        />
      )}

      {trans.open && (
        <BlockchainTransactionDialog
          txid={trans.txid}
          refresh
          handleClose={() => setTrans({ open: false })}
        />
      )}

      <TransactionSideDrawer
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        reference={selectedReference}
      />

      <CryptoOpsDrawer
        open={opsDrawer.open}
        onClose={() => setOpsDrawer({ open: false, row: null })}
        row={opsDrawer.row}
      />
    </Page>
  );
}
